import {AttrsSchema, Component} from 'panel';

import {
  ChartLegendTheme,
  COMPACT_TABLE_LEGEND_LINE_HEIGHT,
  MAX_SEGMENTS_PER_COMPACT_TABLE_ROW,
} from 'common/components/chart-legend/constants';
import {DisplayAnalysisType} from 'common/components/line-chart/enums';
import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import {LINE_CHART_PROP_KEYS, LineChartAttrs, LineChartProps} from 'common/components/line-chart';
import {Section} from 'common/components/items-menu/types';
import {XAxisOptions} from 'common/components/line-chart/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

import template from './index.jade';
import css from './index.styl?inline';

export enum ChartDisplayContext {
  Primary = `primary`,
  DashboardCard = `dashboard-card`,
}

interface ChartAttrs {
  'chart-height': number;
  'display-analysis-type': DisplayAnalysisType;
  'display-context': ChartDisplayContext;
  'has-no-data': boolean;
  'incomplete-from-date': boolean;
  'incomplete-to-date': boolean;
  'is-compact': boolean;
  'legend-sections': Section[];
  'legend-theme': ChartLegendTheme;
  'show-annotations': boolean;
  'trend-unit': TimeUnit;
  'x-axis-options': XAxisOptions;
}

interface LegendLineChartState {
  lineChartProps: Partial<LineChartProps>;
}
export class LegendLineChart extends Component<LegendLineChartState, ChartAttrs> {
  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      'chart-height': `number`,
      'display-analysis-type': {
        type: `string`,
        enum: Object.values(DisplayAnalysisType),
        default: DisplayAnalysisType.Linear,
      },
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'has-no-data': `boolean`,
      'incomplete-from-date': `boolean`,
      'incomplete-to-date': `boolean`,
      'is-compact': `boolean`,
      'legend-sections': {type: `json`},
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'trend-unit': {
        type: `string`,
        default: TimeUnit.Day,
        enum: [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Month, TimeUnit.Week, TimeUnit.Quarter],
        description: `unit of line-chart that will be used as key for annotations map`,
      },
      'x-axis-options': {
        type: `json`,
        description: `x-axis overrides for highcharts of type XAxisOptions`,
      },
    };
  }

  constructor() {
    super();

    for (const key of LINE_CHART_PROP_KEYS) {
      Object.defineProperty(this, key, {
        get: () => this.state.lineChartProps[key],
        set: (value: any) => {
          this.update({
            lineChartProps: {
              ...this.state.lineChartProps,
              [key]: value,
            },
          });
        },
      });
    }
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        lineChartProps: {},
      },
      helpers: {
        ChartLegendTheme,
        ChartDisplayContext,
        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },
        getChartLegendHeight: (): number => {
          switch (this.attr(`legend-theme`)) {
            case ChartLegendTheme.Primary:
              return DEFAULT_CHART_HEIGHT;
            case ChartLegendTheme.CardTable:
              return this.getCompactTableChartLegendHeight();
              break;
            case ChartLegendTheme.CardList: // not supported in line chart card
            default:
              return DEFAULT_CHART_HEIGHT;
          }
        },
        getLineChartProps: () => this.state.lineChartProps,
        getLineChartAttrs: (): Partial<LineChartAttrs> => ({
          'chart-height': this.getChartHeight(),
          'chart-width': null,
          'display-analysis-type': this.attr(`display-analysis-type`),
          'incomplete-from-date': this.attr(`incomplete-from-date`),
          'incomplete-to-date': this.attr(`incomplete-to-date`),
          'show-annotations': this.attr(`show-annotations`),
          'trend-unit': this.attr(`trend-unit`),
          'x-axis-options': this.attr(`x-axis-options`),
          'zoom-type': `x`,
          ...this.getChartDisplayContextAttributes(),
        }),
        hasLegendSection: () => {
          return this.attr(`legend-sections`) && this.attr(`legend-sections`).length > 0;
        },
        getLegendSection: () =>
          this.attr(`display-context`) === ChartDisplayContext.DashboardCard
            ? this.attr(`legend-sections`).map((itemsObj) => ({
                items: itemsObj.items.filter((item) => item.isSelected),
              }))
            : this.attr(`legend-sections`),
      },
    };
  }

  private getChartDisplayContextAttributes(): Partial<LineChartAttrs> {
    switch (this.attr(`display-context`)) {
      case ChartDisplayContext.DashboardCard:
        return {
          'chart-margin-right': 1,
          'chart-margin-left': 0,
          'reflow-interval': 200,
          'spacing-bottom': 8,
          'y-axis-label-alignment': `left`,
          'y-axis-label-horizontal-offset': 2,
          'y-axis-label-vertical-offset': -2,
        };
      case ChartDisplayContext.Primary:
      default:
        return {};
    }
  }

  private getCompactTableChartLegendHeight(): number {
    if (!this.helpers.hasLegendSection()) {
      return 0;
    }
    const selectedSegmentsCount = this.attr(`legend-sections`).reduce(
      (accu, itemsObj) => accu + itemsObj.items.filter((item) => item.isSelected).length,
      0,
    );
    return Math.ceil(selectedSegmentsCount / MAX_SEGMENTS_PER_COMPACT_TABLE_ROW) * COMPACT_TABLE_LEGEND_LINE_HEIGHT;
  }

  private getChartHeight(): number {
    if (this.attr(`chart-height`)) {
      return this.attr(`chart-height`);
    }

    switch (this.attr(`legend-theme`)) {
      case ChartLegendTheme.Primary:
        return DEFAULT_CHART_HEIGHT;
      case ChartLegendTheme.CardTable:
        return this.parentElement.clientHeight - this.getCompactTableChartLegendHeight();
        break;
      case ChartLegendTheme.CardList: // not supported in line chart card
      default:
        return DEFAULT_CHART_HEIGHT;
    }
  }
}

customElements.define(`mp-legend-line-chart`, LegendLineChart);
